import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { useChat } from "../../../providers/chat-provider";

type HeaderSettingsNoCssFallbackProps = {
  showRefreshSection: boolean;
  showLanguageSection: boolean;
  showLegalSection: boolean;
};

export const HeaderSettingsNoCssFallback = ({
  showRefreshSection,
  showLanguageSection,
  showLegalSection,
}: HeaderSettingsNoCssFallbackProps) => {
  const {
    chatbotParams,
    currentLocale,
    onChangeLocale,
    onRefreshConversation,
    selectedLocale,
    defaultLocale,
  } = useChat();
  const t = useTranslations("do-chat-v2");

  const disclaimerText = useMemo(() => {
    return (
      selectedLocale?.disclaimerText ||
      defaultLocale?.disclaimerText ||
      chatbotParams.disclaimerText
    );
  }, [selectedLocale, defaultLocale, chatbotParams.disclaimerText]);

  if (!showRefreshSection && !showLanguageSection && !showLegalSection) return null;

  // `sr-only` is perfect here:
  // - With CSS enabled: it doesn't clutter the UI, but still exists for AT
  // - With CSS disabled: it becomes visible (required by RGAA 10.2)
  return (
    <details id="header-settings-no-css" className="sr-only">
      <summary>{t("chat.header.settings.title")}</summary>

      {showLanguageSection && (
        <section aria-label={t("chat.header.settings.language.title")}>
          <p>
            <strong>{t("chat.header.settings.language.title")}</strong>
          </p>
          <label htmlFor="header-settings-no-css-language">
            {t("chat.header.settings.language.description")}
          </label>
          <div>
            <select
              id="header-settings-no-css-language"
              value={currentLocale ?? ""}
              onChange={(e) => onChangeLocale?.(e.target.value)}
              disabled={!onChangeLocale}
            >
              {Object.keys(chatbotParams.availableLocales || {}).map((locale) => {
                const localeCode = locale.slice(0, 2);
                return (
                  <option key={locale} value={locale}>
                    {t(`chat.header.settings.language.locales.${localeCode}`)}
                  </option>
                );
              })}
            </select>
          </div>
        </section>
      )}

      {showRefreshSection && (
        <section aria-label={t("chat.header.settings.refresh-chatbot.title")}>
          <p>
            <strong>{t("chat.header.settings.refresh-chatbot.title")}</strong>
          </p>
          <p>{t("chat.header.settings.refresh-chatbot.description")}</p>
          <button type="button" onClick={onRefreshConversation} disabled={!onRefreshConversation}>
            {t("chat.header.settings.refresh-chatbot.button")}
          </button>
        </section>
      )}

      {showLegalSection && disclaimerText && (
        <section aria-label={t("chat.header.settings.legal.title")}>
          <p>
            <strong>{t("chat.header.settings.legal.title")}</strong>
          </p>
          <p>{t("chat.header.settings.legal.description")}</p>
          <div>{disclaimerText}</div>
        </section>
      )}
    </details>
  );
};
